import React, { useContext, useEffect } from 'react'
import { connect } from 'react-redux'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import ProtectedRoute from './components/Common/ProtectedRoute'
import setRedirectUrl from './redux/actions/redirection/setRedirectUrl'
import AbmLocalesPage from './pages/Locales/AbmLocalesPage'
import AbmLocalPage from './pages/AbmLocalPage'
import AbmLocalPageLegacy from './pages/AbmLocalPageLegacy'
import AdminLocalPage from './pages/AdminLocalPage'
import AbmProducts from './pages/Products'
import ContractListPage from './pages/Contracts/Vencimientos/ContractsListPage'
import AdminAbmConfigPage from './pages/AdminAbmConfigPage'
import AdminAbmProductosPage from './pages/AdminProducts/ABMProducts'
import ImagenesProductos from './pages/AdminProducts/ImagenesProductos'
import { initGA, logPageView } from './analytics'
import AdminWspBotPage from './pages/WhatsappBot'
import Contactanos from './pages/Contactanos'
import LoginPage from './pages/Login'
import AbmRubrosPage from './pages/Rubros/AbmRubros'
import Notificaciones from './components/Notificaciones/Notificaciones'
import Pedidos from './pages/Pedidos'
import ContratosVendedor from './pages/ContratosVendedor'
import AbmGalerias from './pages/Galerias'
import Stickers from './pages/Stickers'
import Reportes from './pages/Reportes'
import Home from './pages/Home'
import AdminHistorias from './pages/AdminHistorias'
import Estadisticas from './pages/Estadisticas'
import Calificaciones from './pages/Calificaciones'
import Integraciones from './pages/Integraciones'
import { useAppStore } from './store/app-store'
import AbmNotificaciones from './components/AbmNotificaciones'
import OfrecerPremiumDialog from './context/OfrecerPremium/OfrecerPremiumDialog'
import Prediccion from './pages/Rubros/Prediccion/prediccionImg'
import FotosLocales from './pages/Locales/FotosLocales'
import AbmAnuncios from './pages/AbmAnuncios'
import Entrenamiento from './pages/Rubros/Entrenamiento'
import { Helmet } from 'react-helmet-async'
import { TenantsContext } from './context/tenants.context'
import { Config } from '@redux-devtools/extension'
import { LocalContext } from './pages/AdminLocal/context/local.context'
import Facturacion from './pages/Contracts/Facturacion/Facturacion'
import PerfilImpositivo from './pages/Contracts/PerfilImpositivo'
import './styles/main.scss'

/**
 * Actualiza el favicon del documento de manera dinámica
 * Incluye cache busting para Chrome y otros navegadores
 */
const updateFavicon = (faviconUrl: string): void => {
  // Remover todos los favicons existentes
  const existingFavicons = document.querySelectorAll("link[rel*='icon']")
  existingFavicons.forEach((link) => link.remove())

  const createFaviconLink = (rel: string): HTMLLinkElement => {
    const link = document.createElement('link')
    link.type = 'image/x-icon'
    link.rel = rel
    link.href = `${faviconUrl}?v=${Date.now()}`
    return link
  }

  // Agregar nuevos favicons (múltiples rel para mejor compatibilidad)
  document.head.appendChild(createFaviconLink('icon'))
  document.head.appendChild(createFaviconLink('shortcut icon'))
}

const App: React.FC = (props: any) => {
  const { userLogged } = props.logged
  const setCodLocal = useAppStore((state) => state.setCodLocal)
  const { tenant } = useContext(TenantsContext)
  const { setCodLocal: setCodigo } = useContext(LocalContext)

  useEffect(() => {
    initGA('G-FYPXL88XHF')
    logPageView()

    if (userLogged.isLogged) {
      setCodLocal(userLogged.user?.codLocal)
      setCodigo(userLogged.user?.codLocal)
    }
  }, [userLogged, setCodLocal])

  useEffect(() => {
    const faviconConfig = tenant?.config?.find((c: Config) => c.name === 'favicon')
    const faviconUrl = faviconConfig?.value

    if (faviconUrl) {
      updateFavicon(faviconUrl)
    }
  }, [tenant?.config])

  return (
    <Router>
      <Helmet>
        <title>{tenant?.nombrePlataforma || 'Cargando...'}</title>
      </Helmet>
      <Notificaciones />
      <OfrecerPremiumDialog />
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/abm-local" element={<AbmLocalPage />} />
        <Route path="/abm-local-legacy" element={<AbmLocalPageLegacy />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/home"
          element={<ProtectedRoute element={<Home />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />}
        />
        <Route
          path="/abm-products"
          element={
            <ProtectedRoute element={<AbmProducts />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute element={<Integraciones />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/suscripciones"
          element={
            <ProtectedRoute
              element={<ContratosVendedor />}
              isAuthenticated={userLogged.isLogged}
              redirectURL="/login"
            />
          }
        />
        <Route
          path="/stickers"
          element={<ProtectedRoute element={<Stickers />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />}
        />
        <Route
          path="/abm-locales"
          element={
            <ProtectedRoute element={<AbmLocalesPage />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/fotos-locales"
          element={
            <ProtectedRoute element={<FotosLocales />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/historias"
          element={
            <ProtectedRoute element={<AdminHistorias />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/calificaciones"
          element={
            <ProtectedRoute element={<Calificaciones />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/contactanos"
          element={
            <ProtectedRoute element={<Contactanos />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute element={<Integraciones />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />

        <Route
          path="/admin-local/:code"
          element={
            <ProtectedRoute element={<AdminLocalPage />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/abm-config"
          element={
            <ProtectedRoute
              element={<AdminAbmConfigPage />}
              isAuthenticated={userLogged.isLogged}
              redirectURL="/login"
            />
          }
        />
        <Route
          path="/abm-rubros"
          element={
            <ProtectedRoute element={<AbmRubrosPage />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/prediccion-imagen"
          element={
            <ProtectedRoute element={<Prediccion />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/abm-entrenamiento-rubros"
          element={
            <ProtectedRoute element={<Entrenamiento />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/pedidos"
          element={<ProtectedRoute element={<Pedidos />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />}
        />
        <Route
          path="/reportes"
          element={<ProtectedRoute element={<Reportes />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />}
        />
        <Route
          path="/admin-abm-products"
          element={
            <ProtectedRoute
              element={<AdminAbmProductosPage />}
              isAuthenticated={userLogged.isLogged}
              redirectURL="/login"
            />
          }
        />
        <Route
          path="/productos-imagenes"
          element={
            <ProtectedRoute
              element={<ImagenesProductos />}
              isAuthenticated={userLogged.isLogged}
              redirectURL="/login"
            />
          }
        />
        <Route
          path="/contratos-vencimientos"
          element={
            <ProtectedRoute element={<ContractListPage />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/contratos-facturacion"
          element={
            <ProtectedRoute element={<Facturacion />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/perfil-impositivo-incompleto"
          element={
            <ProtectedRoute element={<PerfilImpositivo />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/admin-wsp-bot"
          element={
            <ProtectedRoute element={<AdminWspBotPage />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/abm-galerias"
          element={
            <ProtectedRoute element={<AbmGalerias />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/estadisticas"
          element={
            <ProtectedRoute element={<Estadisticas />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
        <Route
          path="/notificaciones"
          element={
            <ProtectedRoute
              element={<AbmNotificaciones />}
              isAuthenticated={userLogged.isLogged}
              redirectURL="/login"
            />
          }
        />
        <Route
          path="/abm-anuncios"
          element={
            <ProtectedRoute element={<AbmAnuncios />} isAuthenticated={userLogged.isLogged} redirectURL="/login" />
          }
        />
      </Routes>
    </Router>
  )
}

const mapStateToProps = (state: any) => {
  return {
    logged: state.logged,
    theme: state.theme,
    toaster: state.toaster,
    redirection: state.redirection
  }
}

const mapDispatchToProps = {
  setRedirectUrl
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
